import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken } from "../api";
import StickyHead from "../StickyHead";

// 영문 PDF — 한국어 + 영문으로 낸 서류는 따로, 영문만 낸 서류는 기본 파일이 영문이다
const EN_FILE = { QUOTE: "Quotation", CONTRACT: "Contract", INVOICE: "Invoice", PO: "PurchaseOrder" };
const enOnly = (d) => !!d.pdf_key_en && d.pdf_key_en === d.pdf_key;
const hasExtraEn = (d) => !!d.pdf_key_en && d.pdf_key_en !== d.pdf_key;
const pdfName = (d, en) => `${en || enOnly(d) ? EN_FILE[d.doc_type] || "Document" : d.title}_${d.doc_no}.pdf`;

const DOC_LABEL = { QUOTE: "견적서", CONTRACT: "계약서", INVOICE: "인보이스", PO: "발주서" };
const DOC_ROUTE = { QUOTE: "quotes", CONTRACT: "contracts", INVOICE: "invoices", PO: "orders" };

// 임시저장(견적번호 없음) / 발송 / 작성완료 구분
function docState(d) {
  if (!d.doc_no) return { label: "임시저장", cls: "sstoa-tag--gray", draft: true };
  if (d.status === "SENT") return { label: "발송", cls: "", draft: false };
  return { label: "작성", cls: "sstoa-tag--gray", draft: false };
}

export default function DocumentsList() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  async function load() {
    setError("");
    try {
      setRows(await api.get("/generated"));
    } catch (err) { setError(err.message); }
  }
  useEffect(() => { load(); }, []);

  async function fetchPdfUrl(d, en = false) {
    const res = await fetch(`/api/sstoa/generated/${d.id}/file${en ? "?lang=en" : ""}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error("PDF를 불러오지 못했습니다.");
    return URL.createObjectURL(await res.blob());
  }
  async function view(d) {
    try {
      const url = await fetchPdfUrl(d);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(err.message); }
  }
  async function download(d, en = false) {
    try {
      const url = await fetchPdfUrl(d, en);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfName(d, en);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
  }

  async function remove(id) {
    if (!window.confirm("서류를 삭제할까요?")) return;
    try { await api.del(`/generated/${id}`); load(); } catch (err) { setError(err.message); }
  }

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>
        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">생성한 서류</h1>
            <p className="sstoa-sub">견적서·계약서·인보이스 발행 내역입니다.</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Link to="/sstoa/quotes/new" className="sstoa-btn sstoa-btn--sm">견적서</Link>
            <Link to="/sstoa/contracts/new" className="sstoa-btn sstoa-btn--sm">계약서</Link>
            <Link to="/sstoa/invoices/new" className="sstoa-btn sstoa-btn--sm">인보이스</Link>
          </div>
        </div>
      </StickyHead>

      {error && <div className="sstoa-error">{error}</div>}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">생성한 서류가 없습니다. 우측 상단에서 작성하세요.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
          <table className="sstoa-table">
            <thead><tr><th>종류</th><th>문서번호</th><th>업체</th><th>주요항목</th><th>담당자</th><th>이메일</th><th>연락처</th><th>상태</th><th>날짜</th><th></th></tr></thead>
            <tbody>
              {rows.map((d) => {
                const st = docState(d);
                return (
                  <tr key={d.id} style={{ cursor: "default" }}>
                    <td><span className="sstoa-tag">{DOC_LABEL[d.doc_type] || d.doc_type}</span></td>
                    <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {st.draft ? (
                        <span style={{ color: "var(--s-fg-mute)" }}>—</span>
                      ) : (
                        <button className="sstoa-link" style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }} onClick={() => view(d)} title="브라우저에서 보기">{d.doc_no}</button>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{(d.company && d.company.name) || d.client_name || "—"}</td>
                    <td>{d.subject || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{d.contact_name || "—"}</td>
                    <td>{d.contact_email ? <Link className="sstoa-link" to={`/sstoa/email?to=${encodeURIComponent(d.contact_email)}`}>{d.contact_email}</Link> : "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{d.contact_tel ? <a className="sstoa-link" href={`tel:${d.contact_tel.replace(/-/g, "")}`}>{d.contact_tel}</a> : "—"}</td>
                    <td><span className={`sstoa-tag ${st.cls}`}>{st.label}</span></td>
                    <td style={{ whiteSpace: "nowrap" }}>{((d.sent_at || d.updated_at || d.created_at) || "").slice(0, 10)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {st.draft ? (
                        <Link to={`/sstoa/${DOC_ROUTE[d.doc_type]}/new?draft=${d.id}`} className="sstoa-btn sstoa-btn--sm" style={{ marginRight: 6 }}>이어서 작성</Link>
                      ) : (
                        <>
                          <button className="sstoa-btn sstoa-btn--sm" style={{ marginRight: 6 }} onClick={() => download(d)}>{enOnly(d) ? "다운로드 (영문)" : "다운로드"}</button>
                          {hasExtraEn(d) && <button className="sstoa-btn sstoa-btn--sm" style={{ marginRight: 6 }} onClick={() => download(d, true)} title="영문 PDF 다운로드">영문</button>}
                        </>
                      )}
                      <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(d.id)}>삭제</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
